import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import type { InputTaskCategory } from 'interfaces/IInputTask';
import type { SummaryEntityCategory } from '../../../../../redux/slices/InsightsSlice';
import { computeGrossMargin } from '../computeGrossMargin';
import type { SpendMetric } from './computePlanVsActual';
import {
  pairRowsBySide,
  SUMMARY_DATA_SIDE_CATEGORIES,
  type SideBySideRow,
  type SummaryDataSide,
} from './dataBasis';
import {
  resolveCropGroupLabel,
  resolveInputAreaHa,
  resolveInputCrop,
  STEP_TYPE_TO_SUMMARY_ENTITY,
} from './summary2Utils';

export interface CostByCropRow {
  key: string;
  cropLabel: string;
  areaHa: number;
  costs: Partial<Record<SummaryEntityCategory, number>>;
  totalCost: number;
}

/** One crop's Cost by Crop row per side — each side is a separate computeCostByCrop row, never summed. */
export interface CostByCropSideBySideRow extends SideBySideRow<CostByCropRow> {
  cropLabel: string;
}

const compareCropLabels = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { sensitivity: 'base' });

/**
 * One row per resolved crop/variety — cost breakdown by category (Seed,
 * Soil Input, Plant Protection, Seed Dress, Biostimulant, Operation) plus a
 * grand total, for the Cost by Crop section. Interest/Depreciation/
 * Irrigation aren't tracked in this app's data model, so they're not columns
 * here.
 *
 * Reads only the tasks in `categories` — callers pass one category (Plan
 * or Actual) so the same item recorded in both is never added together.
 * Area only counts inputs with a cost in that category.
 */
export const computeCostByCrop = (
  inputs: IInput[],
  gUnits: GUnit[],
  categories: Set<InputTaskCategory>
): CostByCropRow[] => {
  const rows = new Map<string, CostByCropRow>();

  for (const input of inputs) {
    const gm = computeGrossMargin(input, categories, gUnits);
    if (gm.costBreakdown.length === 0) continue;

    const resolved = resolveInputCrop(input);
    const cropLabel = resolveCropGroupLabel(resolved.crop, resolved.variety);
    const areaHa = resolveInputAreaHa(input, gUnits);

    const existing = rows.get(cropLabel) ?? {
      key: cropLabel,
      cropLabel,
      areaHa: 0,
      costs: {},
      totalCost: 0,
    };

    existing.areaHa += areaHa;
    for (const line of gm.costBreakdown) {
      const category = STEP_TYPE_TO_SUMMARY_ENTITY[line.stepType];
      if (!category) continue;
      existing.costs[category] = (existing.costs[category] ?? 0) + line.totalCost;
      existing.totalCost += line.totalCost;
    }

    rows.set(cropLabel, existing);
  }

  return Array.from(rows.values())
    .filter((row) => row.totalCost > 0)
    .sort((a, b) => compareCropLabels(a.cropLabel, b.cropLabel));
};

/**
 * Cost by Crop for each requested side, matched by crop — one side for Plan
 * or Actual, both for Plan vs Actual (paired columns, never summed).
 */
export const computeCostByCropBySide = (
  inputs: IInput[],
  gUnits: GUnit[],
  sides: SummaryDataSide[]
): CostByCropSideBySideRow[] => {
  const rowsBySide: Partial<Record<SummaryDataSide, CostByCropRow[]>> = {};
  for (const side of sides) {
    rowsBySide[side] = computeCostByCrop(
      inputs,
      gUnits,
      SUMMARY_DATA_SIDE_CATEGORIES[side]
    );
  }

  return pairRowsBySide(rowsBySide, (row) => row.key)
    .map((paired) => ({
      ...paired,
      cropLabel: Object.values(paired.sides)[0]?.cropLabel ?? paired.key,
    }))
    .sort((a, b) => compareCropLabels(a.cropLabel, b.cropLabel));
};

/** A Cost by Crop row's (or totals row's) category costs and total, as $ or $/ha. */
export interface CostByCropValues {
  costs: Partial<Record<SummaryEntityCategory, number>>;
  totalCost: number;
}

/**
 * Scale one side's costs for display. $/ha divides by that same side's own
 * area — Plan by Plan area, Actual by Actual area — so the two are never
 * blended. null when $/ha is asked for and the side has no area recorded.
 */
export const resolveCostByCropValues = (
  source: CostByCropValues & { areaHa: number },
  metric: SpendMetric
): CostByCropValues | null => {
  if (metric === 'total') {
    return { costs: source.costs, totalCost: source.totalCost };
  }
  if (source.areaHa <= 0) return null;
  const costs: Partial<Record<SummaryEntityCategory, number>> = {};
  for (const [category, cost] of Object.entries(source.costs)) {
    costs[category as SummaryEntityCategory] = (cost ?? 0) / source.areaHa;
  }
  return { costs, totalCost: source.totalCost / source.areaHa };
};
